import { useEffect, useState } from "react";
import { CalendarClock, History } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import CrestManager from "@/components/CrestManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY_NEXT = { date: "", home: "Sporting de Gijón", away: "", competition: "LaLiga Hypermotion" };
const EMPTY_LAST = { date: "", home: "Sporting de Gijón", away: "", competition: "LaLiga Hypermotion", home_score: "", away_score: "" };

const MatchEditor = () => {
  const [next, setNext] = useState(EMPTY_NEXT);
  const [last, setLast] = useState(EMPTY_LAST);
  const [crest, setCrest] = useState("");
  const [busy, setBusy] = useState(false);
  const field =
    "w-full rounded-xl border border-edge bg-ink2 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-crimson focus:outline-none";

  useEffect(() => {
    API.get("/sporting/match").then(({ data }) => {
      if (data?.next) setNext({ ...EMPTY_NEXT, ...data.next });
      if (data?.last) setLast({ ...EMPTY_LAST, ...data.last, home_score: data.last.home_score ?? "", away_score: data.last.away_score ?? "" });
      setCrest(data?.crest || "");
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await API.put("/admin/sporting-match", {
        next: { ...next },
        last: {
          ...last,
          home_score: last.home_score === "" ? null : Number(last.home_score),
          away_score: last.away_score === "" ? null : Number(last.away_score),
        },
      });
      toast.success("Partido actualizado en la portada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const Row = ({ children }) => <div className="grid gap-3 sm:grid-cols-2">{children}</div>;
  const Field = ({ label, children }) => (
    <div className="space-y-1.5">
      <Label className="font-mono-score text-[11px] uppercase tracking-widest text-slate-400">{label}</Label>
      {children}
    </div>
  );

  return (
    <div className="mt-8 max-w-3xl space-y-8" data-testid="match-editor">
      <div className="rounded-2xl border border-edge bg-ink2 p-6" data-testid="crest-section">
        <p className="mb-2 font-display text-lg font-bold uppercase text-white">Escudo del Sporting</p>
        <p className="mb-4 text-sm text-slate-400">Se muestra en la franja del partido de la portada.</p>
        <div className="flex items-center gap-4">
          {crest ? (
            <img src={crest} alt="Escudo actual" className="h-16 w-16 rounded-xl border border-edge bg-ink object-contain p-1" data-testid="crest-preview" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-dashed border-edge font-mono-score text-xs text-slate-500">Sin escudo</div>
          )}
          <ImageUpload
            onUploaded={async (url) => {
              setCrest(url);
              try {
                await API.put("/admin/sporting-crest", { url });
                toast.success("Escudo actualizado en la portada");
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail));
              }
            }}
          />
        </div>
      </div>

      <CrestManager />

      <div className="rounded-2xl border border-edge bg-ink2 p-6">
        <p className="mb-4 flex items-center gap-2 font-display text-lg font-bold uppercase text-white">
          <CalendarClock className="h-4 w-4 text-crimson" /> Próximo partido        </p>
        <div className="space-y-3">
          <Row>
            <Field label="Fecha">
              <Input data-testid="match-next-date" value={next.date} onChange={(e) => setNext({ ...next, date: e.target.value })} className={field} placeholder="2026-10-26" />
            </Field>
            <Field label="Competición">
              <Input data-testid="match-next-competition" value={next.competition} onChange={(e) => setNext({ ...next, competition: e.target.value })} className={field} />
            </Field>
          </Row>
          <Row>
            <Field label="Equipo local">
              <Input data-testid="match-next-home" value={next.home} onChange={(e) => setNext({ ...next, home: e.target.value })} className={field} />
            </Field>
            <Field label="Equipo visitante">
              <Input data-testid="match-next-away" value={next.away} onChange={(e) => setNext({ ...next, away: e.target.value })} className={field} />
            </Field>
          </Row>
        </div>
      </div>

      <div className="rounded-2xl border border-edge bg-ink2 p-6">
        <p className="mb-4 flex items-center gap-2 font-display text-lg font-bold uppercase text-white">
          <History className="h-4 w-4 text-crimson" /> Último resultado
        </p>
        <div className="space-y-3">
          <Row>
            <Field label="Fecha">
              <Input data-testid="match-last-date" value={last.date} onChange={(e) => setLast({ ...last, date: e.target.value })} className={field} placeholder="2026-10-19" />
            </Field>
            <Field label="Competición">
              <Input data-testid="match-last-competition" value={last.competition} onChange={(e) => setLast({ ...last, competition: e.target.value })} className={field} />
            </Field>
          </Row>
          <Row>
            <Field label="Equipo local">
              <Input data-testid="match-last-home" value={last.home} onChange={(e) => setLast({ ...last, home: e.target.value })} className={field} />
            </Field>
            <Field label="Equipo visitante">
              <Input data-testid="match-last-away" value={last.away} onChange={(e) => setLast({ ...last, away: e.target.value })} className={field} />
            </Field>
          </Row>
          <Row>
            <Field label="Goles local">
              <Input data-testid="match-last-home-score" type="number" value={last.home_score} onChange={(e) => setLast({ ...last, home_score: e.target.value })} className={field} />
            </Field>
            <Field label="Goles visitante">
              <Input data-testid="match-last-away-score" type="number" value={last.away_score} onChange={(e) => setLast({ ...last, away_score: e.target.value })} className={field} />
            </Field>
          </Row>
        </div>
      </div>

      <Button
        onClick={save}
        disabled={busy}
        data-testid="match-save"
        className="rounded-full bg-crimson px-8 py-3 font-display text-base font-bold uppercase tracking-wider hover:bg-glow"
      >
        {busy ? "Guardando..." : "Guardar en portada"}
      </Button>
    </div>
  );
};

export default MatchEditor;
